import { getCookie } from './cookies';

// Base URL is empty in development (uses proxy from package.json)
// In production, use the REACT_APP_API_URL environment variable
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.queryburst.com'
  : '';

//console.log('API base URL:', API_BASE_URL);

// Track last token refresh attempt to prevent rapid successive refreshes
let lastTokenRefreshAttempt = 0;
// Track ongoing refresh to prevent multiple simultaneous refreshes
let refreshPromise = null;

// ---------------------------------------------------------------------------
// Resilience: retry + error sanitisation
// ---------------------------------------------------------------------------
const NETWORK_RETRY_COUNT = 2;
const NETWORK_RETRY_BASE_MS = 1000;

const USER_FRIENDLY_MESSAGES = {
  network: 'Unable to connect. Please check your connection and try again.',
  server: 'Something went wrong on our end. Please try again shortly.',
  timeout: 'The request timed out. Please try again.',
};

function isNetworkError(error) {
  if (error.name === 'TypeError') return true;
  if (error.name === 'AbortError') return false;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed');
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchWithRetry(url, options, retries = NETWORK_RETRY_COUNT) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      if (error.name === 'AbortError' || !isNetworkError(error) || attempt === retries) {
        throw error;
      }
      await sleep(NETWORK_RETRY_BASE_MS * Math.pow(2, attempt));
    }
  }
  throw lastError;
}

function sanitiseServerResponse(response) {
  return new Response(
    JSON.stringify({
      error: USER_FRIENDLY_MESSAGES.server,
      error_type: 'server_error',
      status: response.status,
    }),
    {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

/**
 * Gets the full URL for an API endpoint
 * @param {string} path - The API endpoint path
 * @returns {string} The full URL
 */
export function getApiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

/**
 * Makes an authenticated API request
 * @param {string} url - The API endpoint
 * @param {Object} options - Request options
 * @param {boolean} hasRetried - Whether this request has already been retried (prevents infinite loops)
 * @returns {Promise<Response>}
 */
export async function apiRequest(url, options = {}, hasRetried = false) {
  let accessToken = localStorage.getItem('accessToken');
  //console.log(`[${requestId}] Access token exists: ${!!accessToken}`);
  
  // Note: refresh token is now in HTTP-only cookie, not localStorage
  const csrfToken = getCookie('csrftoken');
  
  // Debug cookie information
  //console.log("All cookies:", document.cookie);
  //console.log("CSRF token from cookie:", csrfToken);
  
  // Default options
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
  };
  
  // Add CSRF token only if it exists
  if (csrfToken) {
    defaultOptions.headers['X-CSRFToken'] = csrfToken;
  }
  
  // Merge options
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  // CSRF token is already handled in defaultOptions above
  
  // Then in your preemptive refresh logic
  if (url.includes('/api/auth/user/')) {
    // 🎯 OPTIMIZATION: Don't attempt refresh if no access token exists
    if (!accessToken) {
      //console.log(`[${requestId}] No access token - skipping refresh attempt for ${url}`);
      // Continue with request without token (will likely get 401, but that's expected for new users)
    } else {
      // Don't attempt refreshes more than once every 5 minutes
      const now = Date.now();
      if (now - lastTokenRefreshAttempt < 5 * 60 * 1000) {
        //console.log('Skipping refresh attempt - too soon since last attempt');
        // Just use existing token
      } else if (isTokenNearExpiry(accessToken)) {
        try {
          //console.log(`[${requestId}] Refreshing token preemptively for ${url}`);
          const refreshResponse = await fetch(getApiUrl('/api/auth/token/refresh/'), {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...(csrfToken && { 'X-CSRFToken': csrfToken }),
            },
            credentials: 'include',
            // Note: refresh token is automatically sent as HTTP-only cookie
          });
          
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            accessToken = refreshData.access;
            localStorage.setItem('accessToken', accessToken);
            //console.log(`[${requestId}] Token refreshed preemptively`);
            
            // Update Authorization header with new token
            mergedOptions.headers['Authorization'] = `Bearer ${accessToken}`;
            lastTokenRefreshAttempt = now;
          } else {
            //console.error(`[${requestId}] Preemptive token refresh failed:`, await refreshResponse.text());
          }
        } catch (error) {
          //console.error(`[${requestId}] Preemptive token refresh error:`, error);
        }
      }
    }
  }
  
  // Add Authorization header if we have a token
  if (accessToken) {
    mergedOptions.headers['Authorization'] = `Bearer ${accessToken}`;
    //console.log(`[${requestId}] Adding Authorization header for ${url}`);
  } else {
    //console.log(`[${requestId}] No access token available for ${url}`);
  }
  
  // Right before the fetch call
  //console.log(`Request headers for ${url}:`, JSON.stringify(mergedOptions.headers));
  
  // Make the request (with automatic retry for network errors)
  try {
    let response = await fetchWithRetry(getApiUrl(url), mergedOptions);
    
    // If the response is 401 Unauthorized, try to refresh using HTTP-only cookie
    // Note: 403 Forbidden usually means permission denied, not expired token
    if (response.status === 401 && !hasRetried) {
      
      // If there's already a refresh in progress, wait for it
      if (refreshPromise) {
        try {
          await refreshPromise;
          // Token should be refreshed now, retry with new token
          const newToken = localStorage.getItem('accessToken');
          if (newToken) {
            mergedOptions.headers['Authorization'] = `Bearer ${newToken}`;
            return apiRequest(url, options, true); // Retry once
          }
        } catch (error) {
          return response; // Return original 401
        }
      } else {
        // Start a new refresh
        refreshPromise = (async () => {
          try {
            const refreshResponse = await fetch(getApiUrl('/api/auth/token/refresh/'), {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                ...(csrfToken && { 'X-CSRFToken': csrfToken }),
              },
              credentials: 'include',
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              localStorage.setItem('accessToken', refreshData.access);
              return true;
            } else {
              localStorage.removeItem('accessToken');
              return false;
            }
          } catch (error) {
            localStorage.removeItem('accessToken');
            return false;
          }
        })();
        
        try {
          const success = await refreshPromise;
          if (success) {
            const newToken = localStorage.getItem('accessToken');
            mergedOptions.headers['Authorization'] = `Bearer ${newToken}`;
            return apiRequest(url, options, true); // Retry once
          }
        } finally {
          refreshPromise = null;
        }
      }
    }
    
    // Sanitise 5xx responses so raw tracebacks never reach callers
    if (response.status >= 500) {
      console.error(`[apiRequest] Server error ${response.status} from ${url}`);
      return sanitiseServerResponse(response);
    }
    
    return response;
  } catch (error) {
    if (error.name === 'AbortError') throw error;

    console.error(`[apiRequest] Network error for ${url}:`, error.message);
    const sanitised = new Error(USER_FRIENDLY_MESSAGES.network);
    sanitised.name = 'NetworkError';
    sanitised.originalError = error;
    throw sanitised;
  }
}

// Add this helper function
function isTokenNearExpiry(token) {
  try {
    // Decode the JWT payload
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if exp claim exists
    if (payload.exp) {
      // Consider "near expiry" as within 5 minutes of expiration
      return payload.exp * 1000 < Date.now() + (5 * 60 * 1000);
    }
    return false;
  } catch (e) {
    //console.error('Error parsing token:', e);
    return true; // Refresh if we can't parse the token
  }
}